<script setup>
import NfoIcon from '#elements/NfoIcon.vue'
import deviceHasHover from '#helpers/deviceFeatures/deviceHasHover.js'
import { usePreferencesStore } from '#stores/usePreferencesStore.js'
import { useMutationObserver } from '@vueuse/core'
import { computed, onMounted, shallowRef, useAttrs, useSlots, useTemplateRef } from 'vue'

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()
const emit = defineEmits(['click'])
const preferences = usePreferencesStore()
const props = defineProps({
	route: { type: [Object, String], default: null },
	loading: { type: Boolean, default: false },
	icon: { type: [String, Array], default: null },
	context: { type: String, default: null },
	hideIcon: { type: Boolean, default: false },
	hideText: { type: Boolean, default: false },
	isActive: { type: Boolean, default: false },
})
const slots = useSlots()

/**
 * @typedef {import('vue').Ref} Ref
 * @typedef {import('vue').ShallowRef} ShallowRef
 * @typedef {import('vue').TemplateRef} TemplateRef
 */

/** @type { TemplateRef } */
const rootEl = useTemplateRef('rootEl')
/** @type { TemplateRef } */
const textEl = useTemplateRef('textEl')
/** @type { ShallowRef } */
const text = shallowRef(null)

const type = computed(() => {
	return props?.route ? 'router-link' : 'button'
})

const hasText = computed(() => {
	return (slots?.default?.()?.length ?? 0) > 0
})

const hasIcon = computed(() => {
	return props.icon !== null
})

const showIcon = computed(() => {
	const userPrefersIconOnly = preferences.get('buttons') === 'icon_only'
	const userPrefersTextOnly = preferences.get('buttons') === 'text_only'
	// do not show the icon by default
	let show = false
	if (hasIcon.value === true) {
		// if there is an icon, show it by default
		show = true
		// if the users prefers icons, show the icon
		if (userPrefersIconOnly) {
			return true
		}
		// if the users prefers text, do not show the icon
		if (userPrefersTextOnly) {
			return false
		}
		// if the button is set to hide the icon, do not show the icon
		if (props.hideIcon) {
			show = false
		}
		// if there is no text, show the icon
		if (!hasText.value) {
			show = true
		}
	}
	return show
})

const showText = computed(() => {
	const userPrefersIconOnly = preferences.get('buttons') === 'icon_only'
	const userPrefersTextOnly = preferences.get('buttons') === 'text_only'
	// do not show text by default
	let show = false
	if (hasText.value) {
		// if there is text, show it by default
		show = true
		// if the users prefers icons, do not show the text
		if (userPrefersIconOnly) {
			return false
		}
		// if the users prefers text, do show the text
		if (userPrefersTextOnly) {
			return true
		}
		// if the button is set to hide text, do not show the text
		if (props.hideText) {
			show = false
		}
		// if there is no icon, DO show the text
		if (!hasIcon.value) {
			show = true
		}
		// if the device has no hover, DO show the text
		if (!deviceHasHover()) {
			show = true
		}
		// show it if for some reason hideText AND hideIcon are on
		if (props.hideIcon && props.hideText) {
			show = true
		}
	}
	return show
})

const newLayout = computed(() => preferences.get('layout') === 'modern')

onMounted(() => {
	text.value = textEl?.value?.innerText
	useMutationObserver(
		rootEl,
		() => {
			text.value = textEl?.value?.innerText
		},
		{ characterData: true, subtree: true },
	)
})
</script>

<template>
	<component
		:is="type"
		ref="rootEl"
		v-tip="hasText && !showText && !attrs['v-tip'] ? text : false"
		:aria-label="hasText && !showText ? text : null"
		:class="[
			context ? [`btn-${context}`] : null,
			{ 'button--is-active': isActive, btn: !newLayout, 'btn--new': newLayout },
		]"
		:title="hasText && !showText ? text : null"
		:to="type === 'router-link' ? route : null"
		:type="type === 'button' ? (attrs?.type ?? 'button') : null"
		role="button"
		v-bind="attrs"
		@click="type === 'button' ? emit('click') : null"
	>
		<Transition name="loader-slide">
			<span v-if="loading" class="loader-slide">
				<nfo-icon name="LoaderCircle" class="animate-spin" />
			</span>
		</Transition>
		<nfo-icon v-if="showIcon" :name="icon" />
		<span v-show="showText" ref="textEl" class="text-nowrap">
			<slot />
		</span>
	</component>
</template>
